import type { TrackingPayload } from './api'

type FbqFunction = (action: string, event: string, params: Record<string, unknown>, options: { eventID: string }) => void

type TrackingWindow = Window & {
  fbq?: FbqFunction
  dataLayer?: unknown[]
}

const PAGE_SOURCES = new Set(['page_url', 'referrer'])
const DEFAULT_UTM = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id']

/**
 * `document.cookie` LANÇA em origem opaca — iframe com `sandbox` sem `allow-same-origin`, ou uma
 * página servida com `Content-Security-Policy: sandbox`. Sem esta guarda a exceção sobe no meio da
 * montagem do rastreamento e o envio trava, com o botão preso em "Enviando…". Aconteceu na
 * pré-visualização de landing do CRM.
 */
function allCookies(): string {
  try {
    return document.cookie || ''
  } catch {
    return ''
  }
}

export function readCookie(name: string): string | undefined {
  const prefix = name + '='
  for (const part of allCookies().split(';')) {
    const item = part.trim()
    if (item.startsWith(prefix)) {
      try {
        return decodeURIComponent(item.slice(prefix.length))
      } catch {
        return item.slice(prefix.length)
      }
    }
  }
  return undefined
}

export function queryParam(name: string): string | null {
  try {
    return new URLSearchParams(window.location.search).get(name)
  } catch {
    return null
  }
}

export function conversionsEnabled(): boolean {
  return queryParam('conversion') !== 'false'
}

export function gaClientIdFromCookie(raw: string | undefined): string | undefined {
  if (!raw) return undefined
  const parts = raw.split('.')
  return parts.length >= 4 ? parts.slice(2).join('.') : undefined
}

export function collectTracking(utmParams: string[]): TrackingPayload {
  const params = new URLSearchParams(window.location.search)
  const fbclid = params.get('fbclid') || undefined
  const cookieFbc = readCookie('_fbc')
  const payload: TrackingPayload = { pageUrl: window.location.href, utm: {} }

  const fbc = cookieFbc || (fbclid ? `fb.1.${Date.now()}.${fbclid}` : undefined)
  const optional: Omit<TrackingPayload, 'pageUrl' | 'utm'> = {
    fbc,
    fbp: readCookie('_fbp'),
    fbclid,
    gclid: params.get('gclid') || undefined,
    gbraid: params.get('gbraid') || undefined,
    wbraid: params.get('wbraid') || undefined,
    gaClientId: gaClientIdFromCookie(readCookie('_ga')),
    referrer: document.referrer || undefined,
  }
  for (const [key, value] of Object.entries(optional)) {
    if (value) (payload as Record<string, unknown>)[key] = value
  }

  const wanted = utmParams.length > 0 ? utmParams : DEFAULT_UTM
  for (const name of wanted) {
    if (PAGE_SOURCES.has(name)) continue
    const value = params.get(name)
    if (value) payload.utm[name] = value
  }
  return payload
}

/**
 * O mesmo id vai para o `fbq` e para o servidor. Sem `crypto.randomUUID` (contexto inseguro, que é
 * o caso de uma página aberta por IP), cai num id aleatório qualquer — o que importa é ser o mesmo
 * dos dois lados, não o formato.
 */
export function newEventId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  } catch {
    // contexto inseguro
  }
  return `pv-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

export function facebookCookies(): { fbc?: string; fbp?: string } {
  const cookies: { fbc?: string; fbp?: string } = {}
  const fbc = readCookie('_fbc')
  const fbp = readCookie('_fbp')
  if (fbc) cookies.fbc = fbc
  if (fbp) cookies.fbp = fbp
  return cookies
}

export function trackPixel(eventName: string, eventId: string): void {
  const w = window as TrackingWindow
  if (typeof w.fbq !== 'function') return
  try {
    w.fbq('track', eventName, {}, { eventID: eventId })
  } catch {
    // pixel de terceiros não pode derrubar o envio
  }
}

export function pushDataLayer(entry: Record<string, unknown>): void {
  const w = window as TrackingWindow
  if (!Array.isArray(w.dataLayer)) return
  try {
    w.dataLayer.push(entry)
  } catch {
    // dataLayer de terceiros não pode derrubar o envio
  }
}
